import { QueryReference, useBackgroundQuery } from "@apollo/client";
import {
  InsightPenaltiesQuery,
  InsightsFacilityMixQuery,
  InsightViolationsQuery,
  GetInsightsNonComplianceQuery,
} from "generated-graphql/graphql";
import {
  NavigateCallback,
  useNavigateWithOmnisearch,
} from "hooks/useNavigateWithOmnisearch";
import { useSuspenseQueryParams } from "hooks/useSuspenseQueryParams";
import { ReactNode, createContext, useContext, useMemo } from "react";
import { INSIGHTS_FACILITY_MIX_QUERY } from "./FacilityMix/FacilityMixTile";
import { INSIGHTS_VIOLATIONS_QUERY } from "./Violations/ViolationsTile";
import { useTenant } from "hooks/useTenant";
import invariant from "invariant";
import { PENALTIES_QUERY } from "./Penalties/PenaltiesTile";
import { GET_INSIGHTS_NON_COMPLIANCE } from "./NonCompliance/NonComplianceTile";

type Filter = {
  programArea: string;
  naicsCode: string;
  naicsType: string;
  violationGraphType: string;
  noncomplianceGraphType: string;
  penaltyGraphType: string;
};

export type OverviewState = {
  filters: Partial<Filter>;
  deferredFilters: Partial<Filter>;
  isPending: boolean;
};

type OverviewContextType = {
  overviewState: OverviewState;
  facilityMixQuery: QueryReference<InsightsFacilityMixQuery>;
  penaltiesQuery: QueryReference<InsightPenaltiesQuery>;
  violationsQuery: QueryReference<InsightViolationsQuery>;
  nonComplianceQuery: QueryReference<GetInsightsNonComplianceQuery>;
  setOverviewState: (cb: (state: Partial<Filter>) => void) => void;
  drillDownToEcho: NavigateCallback<{
    programArea?: string;
    naicsCode?: string;
  }>;
};

const OverviewStateContext = createContext<OverviewContextType | undefined>(
  undefined
);

export function useOverviewState() {
  const context = useContext(OverviewStateContext);
  if (!context) {
    throw new Error("useOverviewState must be used within an OverviewContext");
  }
  return context;
}

export function OverviewContext({ children }: { children: ReactNode }) {
  const { tenantId } = useTenant();
  invariant(tenantId, "tenantId is required");

  const {
    optimisticFilters: filters,
    deferredFilters,
    setFilters,
    isPending,
  } = useSuspenseQueryParams<Filter>();

  // This is a combination of the url filter state and the pending suspense state
  const overviewState = useMemo(
    () => ({ filters, deferredFilters, isPending }),
    [filters, deferredFilters, isPending]
  );

  const drillDownToEcho = useNavigateWithOmnisearch({
    to: "../echo",
    filters: {
      programArea: filters.programArea,
      naicsCode: filters.naicsCode === "Other" ? undefined : filters.naicsCode,
    },
  });

  const [facilityMixQuery] = useBackgroundQuery(INSIGHTS_FACILITY_MIX_QUERY, {
    variables: {
      tenantId,
      programArea: deferredFilters.programArea,
      naicsCode: deferredFilters.naicsCode,
    },
  });

  const [penaltiesQuery] = useBackgroundQuery(PENALTIES_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: {
        programArea: deferredFilters.programArea,
        naicsCode: deferredFilters.naicsCode?.toString(),
      },
    },
  });

  const [violationsQuery] = useBackgroundQuery(INSIGHTS_VIOLATIONS_QUERY, {
    variables: {
      tenantId: tenantId ?? "",
      filter: {
        programArea: deferredFilters.programArea,
        naicsCode: deferredFilters.naicsCode?.toString(),
      },
    },
  });

  const [nonComplianceQuery] = useBackgroundQuery(GET_INSIGHTS_NON_COMPLIANCE, {
    variables: {
      tenantId: tenantId ?? "",
      filter: {
        programArea: deferredFilters.programArea,
        naicsCode: deferredFilters.naicsCode?.toString(),
      },
    },
  });

  const value = useMemo(
    () => ({
      overviewState,
      facilityMixQuery,
      violationsQuery,
      penaltiesQuery,
      nonComplianceQuery,
      setOverviewState: setFilters,
      drillDownToEcho,
    }),
    [
      overviewState,
      facilityMixQuery,
      violationsQuery,
      setFilters,
      drillDownToEcho,
      penaltiesQuery,
      nonComplianceQuery,
    ]
  );

  return (
    <OverviewStateContext.Provider value={value}>
      {children}
    </OverviewStateContext.Provider>
  );
}
